import React, { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import _, { find, get, isEmpty } from "lodash";
import { Button, Typography } from "@mui/material";
import ButtonComponent from "../Button";
import { SellOutlined, RedeemOutlined } from '@mui/icons-material';
import Card from "../Card";
import GiftSmallIcon from "../icons/Gift.svg";
import CalendarIcon from "../icons/Calendar.svg";
import { Price } from "../PriceLine";
import UnlockRewardModal from "../UnlockRewardModal";
import moment from "../../utils/moment-timezone-with-data";
import { selectGift } from "../../store/giftRedeem/actions";
import { getParams, navigateTo, setParams } from "../../utils/location";
import { openNewOrPrevOrderModal } from "../../store/app/actions";
import { isCouponAvailableForGroup } from "../../logic/user/getters/getAvailablePointBankCoupons";

export default function CouponBatches({
  T,
  appStyles,
  currencySymbol,
  onChange,
  saveOrder,
  user,
  app,
  order: { 
    servingOptionType,
    branchId,
    couponsUncheckedByUser,
    couponsCheckedByUser,
    checkoutResponse,
    ...order
  },
  pageContext: {
    businessAppConfiguration,
    pointBankCoupons,
    menuData,
    servingOptions,
    branches,
    location,
  },
  batches,
  couponInstances,
  couponsBatchIndex,
}) {
  const dispatch = useDispatch();

  const batchesData = batches || _.get(checkoutResponse, "couponBatchesResponse.batches", []);
  const couponInstancesData = couponInstances || _.get(checkoutResponse, "couponBatchesResponse.couponInstances", []);
  const couponsBatchIndexData = couponsBatchIndex || _.get(order, "couponsBatchIndex", []);

  const isCouponAvaialbleForGroup = (couponInstance) =>
    isCouponAvailableForGroup(couponInstance, user);

  const rewardPointsBalance = _.get(user, 'loyaltyProfile.data.rewardPointBalance.balance')
 
  const isAvailablePointBankCoupon = (pointBankCoupon) => rewardPointsBalance - (pointBankCoupon.price || 0) >= 0;

  const [availablePointBankCoupons, lockedPointBankCoupons] = _.partition(
    _.filter(pointBankCoupons, isCouponAvaialbleForGroup),
    isAvailablePointBankCoupon,
  );

  const isUnlockRewardEnabled = _.get(businessAppConfiguration, "enableUnlockRewardsFlow");

  const [giftSelected, setGiftSelected] = useState(null);
  const [unlockRewardModalOpen, setUnlockRewardModalOpen] = useState(false);

  const limit = 10;

  const { couponInstanceIds, couponInstanceIdsToDiscount } =
    couponsBatchIndexData > -1 && batchesData[couponsBatchIndexData]
      ? batchesData[couponsBatchIndexData]
      : { couponInstanceIds: [], couponInstanceIdsToDiscount: [] };

  const allCouponInstanceIds = _.map(couponInstancesData, c => _.get(c, "id"));

  const pointBankCouponIds = useMemo(() => _.map(
    pointBankCoupons,
    ({ couponId }) => couponId
  ), [pointBankCoupons]);

  const pointBankCouponGifts = useMemo(() => _.take(
    _.reverse(
      _.filter(user.gifts, (gift) =>
        _.includes(pointBankCouponIds, _.get(gift, "couponInstance.couponId"))
      )
    ),
    3
  ), [user.gifts, pointBankCouponIds]);

  useEffect(() => {
    if (!user.couponToRedeemAfterTrade) return;

    const gift = find(
      user.gifts,
      ({ couponInstance: { couponId } }) =>
        couponId === user.couponToRedeemAfterTrade
    );

    dispatch(selectGift(gift, menuData));
  }, [user.couponToRedeemAfterTrade]);

  const handleRedeemGift = (gift) => {
    dispatch(openNewOrPrevOrderModal({ context: 'coupons', giftToRedeem: gift }));
  };

  const handleUnlockGift = (pointBankCoupon) => {
    setGiftSelected(pointBankCoupon);
    setUnlockRewardModalOpen(true);
  };

  return (
    <Card appStyles={appStyles}>
      {!isEmpty(batchesData) && (
        <>
          <Card.Title
            light
            appStyles={appStyles}
            style={{ display: "flex", justifyContent: "center" }}
          >
            <GiftSmallIcon style={{ width: "24px", height: "24px" }} />
            {T("Rewards")}
          </Card.Title>
          <Card.Content>
            <Card.Content.CheckboxGroup
              name={T("Rewards")}
              limit={limit < 0 ? undefined : limit}
              checked={couponInstanceIds}
              onChange={(checked) => onChange({ checked })}
              appStyles={appStyles}
            >
              {_.map(couponInstancesData, (couponInstance) => (
                <Card.Content.CheckboxOption
                  iconSize={16}
                  iconInnerSize={16}
                  pointColor={appStyles.accentColor}
                  value={couponInstance.id}
                  key={couponInstance.id}
                >
                  <span style={{ paddingTop: 4, width: "100%" }}>
                    <span
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                      }}
                    >
                      <span>{T(couponInstance.coupon.title)}</span>
                      <Price
                        discount
                        value={getDiscountAmount(
                          couponInstance.id,
                          couponInstanceIdsToDiscount
                        )}
                        currencySymbol={currencySymbol}
                      />
                    </span>
                    <span>
                      <small style={{...appStyles.CheckoutRewardsText || { color: "#555" }}}>
                        {T(couponInstance.coupon.description)}
                      </small>
                    </span>
                    <br />
                    <span>
                      <small style={{...appStyles.CheckoutRewardsText || { color: "#555" }}}>
                        {couponInstance.expirationDate && (
                          <span>
                            <CalendarIcon />
                            {` ${T("Valid until:")} ` +
                              moment(
                                new Date(couponInstance.expirationDate)
                              ).format(
                                appStyles.giftsExpirationDateFormat ||
                                  "MMM Do YY"
                              )}
                          </span>
                        )}
                      </small>
                    </span>
                  </span>
                </Card.Content.CheckboxOption>
              ))}
            </Card.Content.CheckboxGroup>
          </Card.Content>
        </>
      )}
      {pointBankCouponGifts && !_.isEmpty(pointBankCouponGifts) && (
        <>
          <Card.Title
            light
            appStyles={appStyles}
            style={{ display: "flex", justifyContent: "center" }}
          >
            <RedeemOutlined />
            {T("Purchased gifts")}
          </Card.Title>
          <Card.Content>
            {_.map(pointBankCouponGifts, (gift) => (
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  margin: "10px",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    maxWidth: "65%",
                  }}
                >
                  <span>{T(_.get(gift, "couponInstance.coupon.title"))}</span>
                </div>
                <Button
                  variant="contained"
                  disabled={allCouponInstanceIds.includes(gift.couponInstance.id)}
                  onClick={() => allCouponInstanceIds.includes(gift.couponInstance.id) ? null : handleRedeemGift(gift)}
                >
                  {allCouponInstanceIds.includes(gift.couponInstance.id) ? couponInstanceIds.includes(gift.couponInstance.id) ? T("Redeemed") : T("Select Above") : T("Redeem")}
                </Button>
              </div>
            ))}
            <ButtonComponent
              linkStyle
              appStyles={appStyles}
              style={{ minWidth: 100, marginTop: 16 }}
              centered
              onClick={() => {
                const giftsLinkTarget = setParams("/gifts", {
                  ...getParams(location),
                  backPath: "/order",
                });
                navigateTo(giftsLinkTarget);
              }}
            >
              <small>{T("More gifts")}</small>
            </ButtonComponent>
          </Card.Content>
        </>
      )}
      {isUnlockRewardEnabled && !_.isEmpty(pointBankCoupons) && (
        <>
          <Card.Title
            light
            appStyles={appStyles}
            style={{ display: "flex", justifyContent: "center" }}
          >
            <SellOutlined />
            {T("Gifts for points")}
          </Card.Title>
          <Card.Content>
            {_.map(
              [...availablePointBankCoupons, ...lockedPointBankCoupons],
              (pointBankCoupon) => (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    margin: "10px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      maxWidth: "65%",
                    }}
                  >
                    <span>{T(pointBankCoupon.coupon.title)}</span>
                    <span style={{ fontSize: "0.9rem" }}>{`${
                      pointBankCoupon.price
                    } points`}</span>
                  </div>
                  <Button
                    disabled={lockedPointBankCoupons.includes(
                      pointBankCoupon
                    )}
                    variant="contained"
                    onClick={() => handleUnlockGift(pointBankCoupon)}
                  >
                    {T("Unlock")}
                  </Button>
                </div>
              )
            )}
            <Typography align="center" sx={{ padding: "10px 0" }}>
              {T(`You have ${rewardPointsBalance} points on your balance`)}
            </Typography>
          </Card.Content>
          {giftSelected && (
            <UnlockRewardModal
              modalOpen={unlockRewardModalOpen}
              closeModal={() =>
                setUnlockRewardModalOpen(!unlockRewardModalOpen)
              }
              T={T}
              appStyles={appStyles}
              giftToUnlock={giftSelected}
              onSuccessfulUnlock={saveOrder}
              shouldSyncGifts
              redeemAfterTrade={!isEmpty(get(giftSelected, "coupon.couponValue.offeredItems"))}
            />
          )}
        </>
      )}
    </Card>
  );
}

const getDiscountAmount = (couponInstanceId, couponInstanceIdsToDiscount) => {
  const couponDiscount = _.find(
    couponInstanceIdsToDiscount,
    (cIToD) => cIToD.couponInstanceId === couponInstanceId,
  );
  return couponDiscount ? couponDiscount.discountAmount : null;
};
